import endOfDay from 'date-fns/endOfDay';
import format from 'date-fns/format';
import getUnixTime from 'date-fns/getUnixTime';
import startOfDay from 'date-fns/startOfDay';
import subDays from 'date-fns/subDays';
import JsPDF from 'jspdf';
import ReportsAPI from 'dashboard/api/reports';
import CSATReports from 'dashboard/api/csatReports';
import SLAReportsAPI from 'dashboard/api/slaReports';
import userActivityReportsAPI from 'dashboard/api/userActivityReports';

const THEMES = {
  dark: {
    background: [8, 20, 38],
    panel: [15, 32, 55],
    panelAlt: [19, 42, 69],
    border: [42, 67, 96],
    text: [241, 245, 249],
    muted: [148, 163, 184],
    blue: [31, 147, 255],
    green: [68, 206, 75],
    violet: [139, 92, 246],
    amber: [250, 204, 21],
    red: [248, 113, 113],
  },
  light: {
    background: [248, 250, 252],
    panel: [255, 255, 255],
    panelAlt: [241, 245, 249],
    border: [203, 213, 225],
    text: [15, 23, 42],
    muted: [71, 85, 105],
    blue: [2, 132, 199],
    green: [22, 163, 74],
    violet: [124, 58, 237],
    amber: [202, 138, 4],
    red: [220, 38, 38],
  },
};

let COLORS = THEMES.dark;
let HEADER_LOGO = null;
const WELLON_HEADER_LOGO = '/brand-assets/wellon-sidebar-logo.svg';

const rasterizeLogo = source =>
  new Promise(resolve => {
    if (!source) {
      resolve(null);
      return;
    }

    const image = new Image();
    const sourceUrl = new URL(source, window.location.origin);
    if (sourceUrl.origin !== window.location.origin) {
      image.crossOrigin = 'anonymous';
    }
    image.onload = () => {
      try {
        const maximumSize = 256;
        const scale = Math.min(
          maximumSize / image.naturalWidth,
          maximumSize / image.naturalHeight,
          1
        );
        const width = Math.max(Math.round(image.naturalWidth * scale), 1);
        const height = Math.max(Math.round(image.naturalHeight * scale), 1);
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d').drawImage(image, 0, 0, width, height);
        resolve({
          data: canvas.toDataURL('image/png'),
          width,
          height,
        });
      } catch {
        resolve(null);
      }
    };
    image.onerror = () => resolve(null);
    image.src = sourceUrl.href;
  });

const loadHeaderLogo = async sources => {
  const uniqueSources = [...new Set(sources.filter(Boolean))];
  return uniqueSources.reduce(
    async (logoPromise, source) => (await logoPromise) || rasterizeLogo(source),
    Promise.resolve(null)
  );
};

const reportPeriod = ({ from, to } = {}) => {
  const endDate = to ? new Date(to * 1000) : endOfDay(new Date());
  const startDate = from
    ? new Date(from * 1000)
    : startOfDay(subDays(endDate, 6));

  return {
    from: getUnixTime(startDate),
    to: getUnixTime(endDate),
    label: `${format(startDate, 'dd/MM/yyyy')} a ${format(endDate, 'dd/MM/yyyy')}`,
  };
};

const valueOf = (item = {}) => item.value || item.count || 0;

const safeRequest = async request => {
  try {
    const response = await request();
    return response?.data || null;
  } catch {
    return null;
  }
};

const number = value => Number(value || 0).toLocaleString('pt-BR');

const percentage = value => `${Math.round(Number(value || 0))}%`;

const duration = seconds => {
  const value = Math.round(Number(seconds || 0));
  if (!value) return '0 s';

  const minutes = Math.floor(value / 60);
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  const remainingSeconds = value % 60;

  if (hours) return `${hours}h ${remainingMinutes}min`;
  if (minutes) return `${minutes}min ${remainingSeconds}s`;
  return `${remainingSeconds}s`;
};

const durationInMinutes = minutes => duration(Number(minutes || 0) * 60);

const parseEntityCsv = async (csv, type) => {
  if (typeof csv !== 'string' || !csv.trim()) return [];

  const XLSX = await import('xlsx');
  const workbook = XLSX.read(csv, { type: 'string' });
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  const rows = XLSX.utils
    .sheet_to_json(sheet, { header: 1, defval: '' })
    .filter(row => row.some(value => String(value).trim()));

  return rows.slice(2).map(row => ({
    name: row[0],
    type: type === 'inbox' ? row[1] : '',
    conversations_count: Number(type === 'inbox' ? row[2] : row[1]) || 0,
  }));
};

const normalizeCsatMetrics = metrics => {
  const ratingsCount = metrics?.ratings_count || {};
  const totalResponseCount = Number(metrics?.total_count || 0);
  const totalSentMessagesCount = Number(
    metrics?.total_sent_messages_count || 0
  );
  const positiveResponses =
    Number(ratingsCount['4'] || 0) + Number(ratingsCount['5'] || 0);

  return {
    ratingsCount,
    totalResponseCount,
    totalSentMessagesCount,
    satisfactionScore: totalResponseCount
      ? (positiveResponses / totalResponseCount) * 100
      : 0,
    responseRate: totalSentMessagesCount
      ? (totalResponseCount / totalSentMessagesCount) * 100
      : 0,
  };
};

export const normalizeSlaReports = (reports = []) =>
  reports.map(report => ({
    conversation:
      `#${report.conversation?.id || '-'} - ${report.conversation?.contact?.name || 'Sem contato'}`.slice(
        0,
        26
      ),
    policy: (report.applied_sla?.sla_name || '-').slice(0, 18),
    agent: (report.conversation?.assignee?.name || '-').slice(0, 16),
    misses:
      (report.sla_events || [])
        .map(event => event.event_type?.toUpperCase())
        .filter(Boolean)
        .join(', ') || '-',
  }));

export const getHeaderLogoSources = ({
  globalConfig = {},
  visibleBrandLogo,
} = {}) => [
  globalConfig.API_CHANNEL_THUMBNAIL,
  globalConfig.apiChannelThumbnail,
  visibleBrandLogo,
  WELLON_HEADER_LOGO,
];

const setColor = (doc, color) => doc.setTextColor(...color);

const drawPage = (doc, title, subtitle, page) => {
  const width = doc.internal.pageSize.getWidth();
  const height = doc.internal.pageSize.getHeight();

  doc.setFillColor(...COLORS.background);
  doc.rect(0, 0, width, height, 'F');
  doc.setFillColor(...COLORS.blue);
  doc.rect(0, 0, 7, height, 'F');
  doc.setFillColor(...COLORS.panel);
  doc.roundedRect(16, 16, width - 32, 24, 3, 3, 'F');
  let titleX = 24;
  if (HEADER_LOGO) {
    const maximumWidth = 28;
    const maximumHeight = 14;
    const scale = Math.min(
      maximumWidth / HEADER_LOGO.width,
      maximumHeight / HEADER_LOGO.height
    );
    const logoWidth = HEADER_LOGO.width * scale;
    const logoHeight = HEADER_LOGO.height * scale;
    doc.addImage(
      HEADER_LOGO.data,
      'PNG',
      22,
      28 - logoHeight / 2,
      logoWidth,
      logoHeight
    );
    titleX = 27 + logoWidth;
  }
  setColor(doc, COLORS.text);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.text(title, titleX, 30);
  setColor(doc, COLORS.muted);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text(subtitle, titleX, 36);
  doc.text(`Relatorios Wellon | ${page}`, width - 24, 30, { align: 'right' });
};

const drawCard = (doc, { x, y, width, label, value, detail, accent }) => {
  doc.setFillColor(...COLORS.panel);
  doc.roundedRect(x, y, width, 40, 3, 3, 'F');
  doc.setDrawColor(...COLORS.border);
  doc.roundedRect(x, y, width, 40, 3, 3, 'S');
  doc.setFillColor(...accent);
  doc.roundedRect(x + 5, y + 6, 3, 28, 1, 1, 'F');
  setColor(doc, COLORS.muted);
  doc.setFontSize(8);
  doc.text(label, x + 13, y + 12);
  setColor(doc, COLORS.text);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.text(String(value), x + 13, y + 25);
  setColor(doc, COLORS.muted);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.text(detail || '', x + 13, y + 33);
};

const drawSection = (doc, title, y) => {
  setColor(doc, COLORS.text);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text(title, 18, y);
  doc.setDrawColor(...COLORS.border);
  doc.line(18, y + 4, 192, y + 4);
};

const drawLineChart = (doc, series, y) => {
  const x = 24;
  const width = 162;
  const height = 56;
  const values = series.flatMap(item => item.values);
  const maximum = Math.max(...values, 1);

  doc.setDrawColor(...COLORS.border);
  [0, 0.5, 1].forEach(position => {
    const lineY = y + height - height * position;
    doc.line(x, lineY, x + width, lineY);
  });

  series.forEach((item, seriesIndex) => {
    doc.setDrawColor(...item.color);
    doc.setFillColor(...item.color);
    item.values.forEach((value, index) => {
      if (!index) return;
      const previous = item.values[index - 1];
      const previousX =
        x + ((index - 1) / Math.max(item.values.length - 1, 1)) * width;
      const currentX =
        x + (index / Math.max(item.values.length - 1, 1)) * width;
      const previousY = y + height - (previous / maximum) * height;
      const currentY = y + height - (value / maximum) * height;
      doc.line(previousX, previousY, currentX, currentY);
      doc.circle(currentX, currentY, 1.2, 'F');
    });
    const legendX = 24 + seriesIndex * 52;
    doc.setFillColor(...item.color);
    doc.circle(legendX, y - 8, 1.8, 'F');
    setColor(doc, COLORS.muted);
    doc.setFontSize(7);
    doc.text(item.label, legendX + 4, y - 6.2);
  });
};

const drawRows = (doc, rows, y, columns) => {
  const rowHeight = 8;
  const maxRows = Math.min(rows.length, 11);

  columns.forEach(column => {
    setColor(doc, COLORS.muted);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.text(column.label, column.x, y);
  });

  rows.slice(0, maxRows).forEach((row, index) => {
    const rowY = y + 6 + index * rowHeight;
    if (index % 2 === 0) {
      doc.setFillColor(...COLORS.panelAlt);
      doc.rect(18, rowY - 4, 174, rowHeight, 'F');
    }
    columns.forEach(column => {
      setColor(doc, COLORS.text);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      const rawValue = column.value(row);
      const text =
        rawValue === null || rawValue === undefined || rawValue === ''
          ? '-'
          : String(rawValue);
      doc.text(text.slice(0, 42), column.x, rowY);
    });
  });
};

const addOverviewPage = (doc, data, period, page) => {
  drawPage(doc, 'Visao geral', period.label, page);
  const account = data.accountConversations || {};
  const agents =
    data.agentConversations?.payload || data.agentConversations || [];
  const metrics = [
    ['Abertas', account.open || account.open_count || 0, COLORS.blue],
    ['Aguardando resposta', account.unattended || 0, COLORS.amber],
    ['Resolvidas hoje', account.resolved_today || 0, COLORS.green],
    [
      'Agentes com volume',
      Array.isArray(agents) ? agents.length : 0,
      COLORS.violet,
    ],
  ];
  metrics.forEach(([label, value, accent], index) => {
    drawCard(doc, {
      x: 18 + (index % 2) * 88,
      y: 52 + Math.floor(index / 2) * 48,
      width: 82,
      label,
      value: number(value),
      detail: 'Atualizacao em tempo real',
      accent,
    });
  });
  drawSection(doc, 'Conversas por agente', 158);
  drawRows(doc, Array.isArray(agents) ? agents : [], 170, [
    { label: 'AGENTE', x: 22, value: row => row.agent?.name || row.name },
    { label: 'ABERTAS', x: 116, value: row => row.metric?.open ?? 0 },
    {
      label: 'AGUARDANDO',
      x: 145,
      value: row => row.metric?.unattended ?? 0,
    },
    {
      label: 'RESOLVIDAS',
      x: 174,
      value: row => row.metric?.resolved_today ?? 0,
    },
  ]);
};

const addConversationPage = (doc, data, period, page) => {
  drawPage(doc, 'Conversas', period.label, page);
  const summary = data.summary || {};
  const cards = [
    ['Conversas', number(summary.conversations_count), COLORS.blue],
    [
      'Mensagens recebidas',
      number(summary.incoming_messages_count),
      COLORS.blue,
    ],
    [
      'Mensagens enviadas',
      number(summary.outgoing_messages_count),
      COLORS.violet,
    ],
    ['Resolvidas', number(summary.resolutions_count), COLORS.green],
  ];
  cards.forEach(([label, value, accent], index) => {
    drawCard(doc, {
      x: 18 + (index % 2) * 88,
      y: 52 + Math.floor(index / 2) * 48,
      width: 82,
      label,
      value,
      detail: 'Periodo selecionado',
      accent,
    });
  });
  drawSection(doc, 'Evolucao no periodo', 156);
  drawLineChart(
    doc,
    [
      {
        label: 'Recebidas',
        color: COLORS.blue,
        values: data.incoming.map(valueOf),
      },
      {
        label: 'Conversas',
        color: COLORS.green,
        values: data.conversations.map(valueOf),
      },
      {
        label: 'Resolvidas',
        color: COLORS.red,
        values: data.resolutions.map(valueOf),
      },
    ],
    176
  );
  drawSection(doc, 'Eficiencia operacional', 248);
  drawRows(
    doc,
    [
      {
        label: 'Tempo de primeira resposta',
        value: duration(summary.avg_first_response_time),
      },
      {
        label: 'Tempo de espera do cliente',
        value: duration(summary.reply_time),
      },
      {
        label: 'Tempo de resolucao',
        value: duration(summary.avg_resolution_time),
      },
    ],
    260,
    [
      { label: 'METRICA', x: 22, value: row => row.label },
      { label: 'VALOR', x: 130, value: row => row.value },
    ]
  );
};

const addCsatPage = (doc, data, period, page) => {
  drawPage(doc, 'CSAT', period.label, page);
  const metrics = data.metrics || {};
  const total = metrics.totalResponseCount || 0;
  const cards = [
    ['CSAT geral', percentage(data.satisfactionScore), COLORS.green],
    ['Respostas', number(total), COLORS.blue],
    ['Taxa de resposta', percentage(data.responseRate), COLORS.violet],
    [
      'Contatos avaliados',
      number(metrics.totalSentMessagesCount),
      COLORS.amber,
    ],
  ];
  cards.forEach(([label, value, accent], index) => {
    drawCard(doc, {
      x: 18 + (index % 2) * 88,
      y: 52 + Math.floor(index / 2) * 48,
      width: 82,
      label,
      value,
      detail: 'Periodo selecionado',
      accent,
    });
  });
  drawSection(doc, 'Distribuicao das avaliacoes', 156);
  const ratings = [5, 4, 3, 2, 1].map(rating => ({
    rating,
    count: metrics.ratingsCount?.[rating] || 0,
  }));
  drawRows(doc, ratings, 169, [
    { label: 'AVALIACAO', x: 22, value: row => `${row.rating}/5` },
    { label: 'RESPOSTAS', x: 105, value: row => number(row.count) },
    {
      label: 'PARTICIPACAO',
      x: 150,
      value: row => percentage(total ? (row.count / total) * 100 : 0),
    },
  ]);
};

const addBotPage = (doc, data, period, page) => {
  drawPage(doc, 'Robos', period.label, page);
  const metrics = data.metrics || {};
  const summary = data.summary || {};
  const cards = [
    ['Conversas iniciadas', number(metrics.conversation_count), COLORS.blue],
    ['Respostas do bot', number(metrics.message_count), COLORS.green],
    ['Taxa de resolucao', percentage(metrics.resolution_rate), COLORS.violet],
    ['Taxa de transferencia', percentage(metrics.handoff_rate), COLORS.amber],
  ];
  cards.forEach(([label, value, accent], index) => {
    drawCard(doc, {
      x: 18 + (index % 2) * 88,
      y: 52 + Math.floor(index / 2) * 48,
      width: 82,
      label,
      value,
      detail: 'Periodo selecionado',
      accent,
    });
  });
  drawSection(doc, 'Resumo de performance', 156);
  drawRows(
    doc,
    [
      {
        label: 'Resolvidas pelo bot',
        value: number(summary.bot_resolutions_count),
      },
      {
        label: 'Transferidas para agentes',
        value: number(summary.bot_handoffs_count),
      },
      {
        label: 'Em andamento',
        value: number(
          Math.max(
            Number(metrics.conversation_count || 0) -
              Number(summary.bot_resolutions_count || 0) -
              Number(summary.bot_handoffs_count || 0),
            0
          )
        ),
      },
    ],
    170,
    [
      { label: 'INDICADOR', x: 22, value: row => row.label },
      { label: 'VOLUME', x: 135, value: row => row.value },
    ]
  );
};

const addEntityPage = (doc, title, rows, period, page) => {
  drawPage(doc, title, period.label, page);
  const values = Array.isArray(rows) ? rows : rows?.payload || [];
  drawCard(doc, {
    x: 18,
    y: 52,
    width: 174,
    label: 'Registros no periodo',
    value: number(values.length),
    detail: 'Dados consolidados pela API de relatorios',
    accent: COLORS.blue,
  });
  drawSection(doc, `Detalhamento de ${title.toLowerCase()}`, 112);
  drawRows(doc, values, 125, [
    {
      label: 'NOME',
      x: 22,
      value: row => row.name || row.title || row.label || row.agent?.name,
    },
    {
      label: 'CONVERSAS',
      x: 122,
      value: row => row.count ?? row.total ?? row.conversations_count ?? 0,
    },
    { label: 'TIPO', x: 160, value: row => row.type || row.status || '-' },
  ]);
};

const addSlaPage = (doc, metrics, reports, period, page) => {
  drawPage(doc, 'SLA', period.label, page);
  const cards = [
    ['Conversas com SLA', number(metrics?.total_applied_slas), COLORS.blue],
    ['SLA cumprido', metrics?.hit_rate || '0%', COLORS.green],
    ['Quebras de SLA', number(metrics?.number_of_sla_misses), COLORS.red],
  ];
  cards.forEach(([label, value, accent], index) => {
    drawCard(doc, {
      x: 18 + index * 58,
      y: 52,
      width: 54,
      label,
      value,
      detail: 'Periodo selecionado',
      accent,
    });
  });

  drawSection(doc, 'Conversas com quebra de SLA', 112);
  drawRows(doc, normalizeSlaReports(reports), 125, [
    {
      label: 'CONVERSA',
      x: 22,
      value: row => row.conversation,
    },
    {
      label: 'POLITICA',
      x: 82,
      value: row => row.policy,
    },
    {
      label: 'AGENTE',
      x: 126,
      value: row => row.agent,
    },
    {
      label: 'QUEBRAS',
      x: 166,
      value: row => row.misses,
    },
  ]);
};

const addActivityPage = (doc, data, period, page) => {
  drawPage(doc, 'Atividade do usuario', period.label, page);
  const summary = data.analytics?.user_summary || [];
  drawCard(doc, {
    x: 18,
    y: 52,
    width: 174,
    label: 'Agentes com atividade no periodo',
    value: number(summary.length),
    detail: 'Ranking e resumo de atividade',
    accent: COLORS.violet,
  });
  drawSection(doc, 'Resumo por agente', 112);
  drawRows(doc, summary, 125, [
    {
      label: 'AGENTE',
      x: 22,
      value: row => row.name || row.agent_name || row.user_name,
    },
    {
      label: 'PAUSAS',
      x: 120,
      value: row => row.total_pause_sessions ?? 0,
    },
    {
      label: 'DURACAO',
      x: 156,
      value: row => durationInMinutes(row.total_pause_duration_minutes),
    },
  ]);
};

export const generateAllReportsPdf = async periodOptions => {
  const isDark = document.body.classList.contains('dark');
  COLORS = isDark ? THEMES.dark : THEMES.light;
  const globalConfig = window.globalConfig || {};
  const visibleBrandLogo = document.querySelector('aside img')?.currentSrc;
  HEADER_LOGO = await loadHeaderLogo(
    getHeaderLogoSources({ globalConfig, visibleBrandLogo })
  );

  const period = reportPeriod(periodOptions);
  const reportParams = {
    from: period.from,
    to: period.to,
    groupBy: 'day',
    businessHours: false,
    conversationDirection: 'all',
  };
  const activityParams = {
    since: format(new Date(period.from * 1000), 'yyyy-MM-dd'),
    until: format(new Date(period.to * 1000), 'yyyy-MM-dd'),
  };

  const [
    accountConversations,
    agentConversations,
    summary,
    incoming,
    conversations,
    resolutions,
    csatMetrics,
    botMetrics,
    botSummary,
    agentsInboxes,
    labels,
    inboxes,
    teams,
    slaMetrics,
    slaReports,
    activityAnalytics,
  ] = await Promise.all([
    safeRequest(() => ReportsAPI.getConversationMetric('account')),
    safeRequest(() => ReportsAPI.getConversationMetric('agent')),
    safeRequest(() =>
      ReportsAPI.getSummary(
        period.from,
        period.to,
        'account',
        null,
        'day',
        false,
        'all'
      )
    ),
    safeRequest(() =>
      ReportsAPI.getReports({
        metric: 'incoming_messages_count',
        ...reportParams,
      })
    ),
    safeRequest(() =>
      ReportsAPI.getReports({ metric: 'conversations_count', ...reportParams })
    ),
    safeRequest(() =>
      ReportsAPI.getReports({ metric: 'resolutions_count', ...reportParams })
    ),
    safeRequest(() =>
      CSATReports.getMetrics({
        from: period.from,
        to: period.to,
        conversationDirection: 'all',
      })
    ),
    safeRequest(() => ReportsAPI.getBotMetrics(reportParams)),
    safeRequest(() => ReportsAPI.getBotSummary(reportParams)),
    safeRequest(() => ReportsAPI.getAgentsInboxes(reportParams)),
    safeRequest(() => ReportsAPI.getLabelReports(reportParams)),
    safeRequest(() => ReportsAPI.getInboxReports(reportParams)),
    safeRequest(() => ReportsAPI.getTeamReports(reportParams)),
    safeRequest(() =>
      SLAReportsAPI.getMetrics({ from: period.from, to: period.to })
    ),
    safeRequest(() =>
      SLAReportsAPI.get({ from: period.from, to: period.to, page: 1 })
    ),
    safeRequest(() => userActivityReportsAPI.getAnalytics(activityParams)),
  ]);

  const [labelRows, inboxRows, teamRows] = await Promise.all([
    parseEntityCsv(labels, 'label'),
    parseEntityCsv(inboxes, 'inbox'),
    parseEntityCsv(teams, 'team'),
  ]);
  const normalizedCsatMetrics = normalizeCsatMetrics(csatMetrics);

  const doc = new JsPDF({ unit: 'mm', format: 'a4' });
  addOverviewPage(doc, { accountConversations, agentConversations }, period, 1);
  doc.addPage();
  addConversationPage(
    doc,
    {
      summary,
      incoming: incoming || [],
      conversations: conversations || [],
      resolutions: resolutions || [],
    },
    period,
    2
  );
  doc.addPage();
  addCsatPage(
    doc,
    {
      metrics: normalizedCsatMetrics,
      satisfactionScore: normalizedCsatMetrics.satisfactionScore,
      responseRate: normalizedCsatMetrics.responseRate,
    },
    period,
    3
  );
  doc.addPage();
  addBotPage(doc, { metrics: botMetrics, summary: botSummary }, period, 4);
  doc.addPage();
  addEntityPage(
    doc,
    'Agentes e caixas de entrada',
    agentsInboxes?.rows || [],
    period,
    5
  );
  doc.addPage();
  addEntityPage(doc, 'Marcadores', labelRows, period, 6);
  doc.addPage();
  addEntityPage(doc, 'Caixas de entrada', inboxRows, period, 7);
  doc.addPage();
  addEntityPage(doc, 'Times', teamRows, period, 8);
  doc.addPage();
  addSlaPage(doc, slaMetrics, slaReports?.payload || [], period, 9);
  doc.addPage();
  addActivityPage(doc, { analytics: activityAnalytics }, period, 10);
  doc.save(`relatorios-wellon-${format(new Date(), 'yyyy-MM-dd')}.pdf`);
};
